import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useMemo } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import DateRangePicker from '~components/Filters/DateRangePicker';
import JobStatusChip from '~components/Job/JobStatusChip';
import { Job } from '~hooks/useJob/models';
import useDeletedRecordsCursor from '~pages/Settings/DeletedRecords/useDeletedRecordsCursor';
import useDeletedRecordsDateFilter from '~pages/Settings/DeletedRecords/useDeletedRecordsDateFilter';
import useDiscardedJobsQuery from '~queries/useDiscardedJobsQuery';

interface DeletedJobsTabProps {
  companyId: string;
}

export default function DeletedJobsTab({ companyId }: DeletedJobsTabProps) {
  const { appliedRange, applyRange, draftRange, selectRange } =
    useDeletedRecordsDateFilter();

  // Whole-day boundaries must resolve in the company timezone or the window shifts a day.
  const startDateParam = appliedRange.startDate
    ? dayjs.tz(appliedRange.startDate.format('YYYY-MM-DD')).startOf('day').toISOString()
    : undefined;
  const endDateParam = appliedRange.endDate
    ? dayjs.tz(appliedRange.endDate.format('YYYY-MM-DD')).endOf('day').toISOString()
    : undefined;

  const filtersKey = JSON.stringify([companyId, startDateParam, endDateParam]);
  const { cursor, goToPage, paginationForDisplay } = useDeletedRecordsCursor(filtersKey);

  const { isFetching, jobs, pagination } = useDiscardedJobsQuery({
    companyId,
    cursor: cursor.cursor,
    direction: cursor.direction,
    endDate: endDateParam,
    startDate: startDateParam,
  });

  const columns = useMemo<Array<DataTableColumn<Job>>>(
    () => [
      {
        field: 'jobId',
        headerName: t('settings.deleted_records.columns.trd_id'),
        valueGetter: (job) => job.jobId,
      },
      {
        field: 'orderId',
        headerName: t('settings.deleted_records.columns.order_number'),
        valueGetter: (job) => job.orderId,
      },
      {
        field: 'driver',
        headerName: t('form_fields.driver'),
        valueGetter: (job) => job.driver?.fullName,
      },
      {
        field: 'vendor',
        headerName: t('form_fields.vendor'),
        // A job's vendor comes from its vendor-type assignment, not the job itself.
        valueGetter: (job) => job.vendorJobAssignment?.vendorAccount?.name,
      },
      {
        field: 'material',
        headerName: t('form_fields.material'),
        valueGetter: (job) => job.material?.name,
      },
      {
        field: 'jobStartAt',
        headerName: t('settings.deleted_records.columns.job_start'),
        valueGetter: (job) =>
          job.jobStartAt ? dayjs.tz(job.jobStartAt).format('MM/DD/YYYY') : '',
      },
      {
        field: 'status',
        headerName: t('form_fields.status'),
        renderCell: (job) => <JobStatusChip hideDropdownArrow jobState={job.status} />,
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        {/* Labelled for jobs.job_start_at, the scheduled date — not the deletion date. */}
        <Typography sx={{ color: 'text.secondary', fontSize: '12px' }}>
          {t('settings.deleted_records.filters.job_start_date')}
        </Typography>
        <DateRangePicker
          allowAllDates
          showShortcuts
          endDate={draftRange.endDate}
          startDate={draftRange.startDate}
          onAnchorClose={applyRange}
          onDateSelect={selectRange}
        />
      </Box>

      <DataTable
        id="deleted-records-jobs-grid"
        columns={columns}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isFetching}
            pagination={paginationForDisplay(pagination)}
            onNavigate={(direction) => goToPage(direction, pagination)}
          />
        }
        loading={isFetching}
        noRowsOverlayText={t('settings.deleted_records.no_deleted_jobs')}
        rows={jobs}
      />
    </Box>
  );
}
